/**
 * Text out of a PDF chord chart. Most of these carry a real text layer — pull
 * that directly and it's exact, no OCR guesswork involved.
 *
 * A scanned PDF (a photographed page saved as PDF, no text layer) has nothing
 * to pull, so the caller gets an empty string back and falls through to
 * rendering the first page to a canvas and running it through the same OCR
 * path a photo import uses.
 *
 * pdfjs-dist is loaded lazily, same reasoning as tesseract.js in ocr.js — it's
 * over a megabyte, and routes in this app aren't code-split, so a static
 * import here would have put it in the main bundle for everyone, not just
 * whoever imports a PDF. The worker is a Vite-bundled, same-origin asset
 * (?url import) rather than pdf.js's own CDN default — the CSP here is
 * `default-src 'self'` with no wildcard, so a cross-origin worker would be
 * silently blocked in production.
 */
async function loadPdfjs() {
  const [pdfjsLib, { default: pdfWorkerUrl }] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ])
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl
  return pdfjsLib
}

export async function extractPdfText(file) {
  const pdfjsLib = await loadPdfjs()
  const buf = await file.arrayBuffer()
  const doc = await pdfjsLib.getDocument({ data: buf }).promise
  const pages = []
  for (let i = 1; i <= doc.numPages; i++) {
    const page = await doc.getPage(i)
    const content = await page.getTextContent()
    // getTextContent() returns items with no line structure of its own — a
    // section label and the lyric under it are just two items in a flat list.
    // hasEOL is pdf.js's own layout analysis (the same one it uses to build
    // the selectable text layer over a rendered page) saying a line actually
    // ended there. Joining every item with a bare space, as an earlier version
    // of this did, collapsed an entire page — verses, chorus and all — into
    // one run-on line, because nothing told parseSheetFile where a line broke.
    let text = ''
    for (const item of content.items) {
      if (typeof item.str !== 'string') continue // TextMarkedContent has no str
      text += item.str
      text += item.hasEOL ? '\n' : ''
    }
    pages.push(text)
  }
  return pages.join('\n\n')
}

/** First page only — a chord chart is rarely more than one or two pages, and
 * OCR-ing a whole scanned booklet is a cost worth asking about, not assuming. */
export async function rasterizeFirstPage(file) {
  const pdfjsLib = await loadPdfjs()
  const buf = await file.arrayBuffer()
  const doc = await pdfjsLib.getDocument({ data: buf }).promise
  const page = await doc.getPage(1)
  const viewport = page.getViewport({ scale: 2 })
  const canvas = document.createElement('canvas')
  canvas.width = viewport.width
  canvas.height = viewport.height
  await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}
